/**
 * Custom hook for Sales/WindoorCraft state management
 * Extraído de App.js para mejor mantenibilidad
 */
import { useState, useCallback } from "react";
import api from "../lib/axios";

// API URL is a constant - doesn't need to be in dependency arrays
const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useSalesState() {
  // ==================== Core Sales Data ====================
  const [quoteTypes, setQuoteTypes] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [materials, setMaterials] = useState(["Aluminio", "uPVC"]);
  const [aluminumSeries, setAluminumSeries] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [salesFactories, setSalesFactories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // ==================== Sales Filters ====================
  const [salesProjectFilter, setSalesProjectFilter] = useState("all");
  const [salesGlassFilter, setSalesGlassFilter] = useState("all");
  const [salesExtrasFilter, setSalesExtrasFilter] = useState("all");
  const [availableSalesGlassSpecs, setAvailableSalesGlassSpecs] = useState([]);
  const [availableSalesExtrasSpecs, setAvailableSalesExtrasSpecs] = useState([]);
  
  // ==================== Type Detail Modal ====================
  const [selectedType, setSelectedType] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // ==================== Add Series/Quote Modals ====================
  const [isAddSeriesOpen, setIsAddSeriesOpen] = useState(false);
  const [isAddQuoteOpen, setIsAddQuoteOpen] = useState(false);
  const [newSeriesName, setNewSeriesName] = useState("");
  const [addQuoteType, setAddQuoteType] = useState(null);
  const [newQuote, setNewQuote] = useState({ width: "", height: "", cost: "", notes: "" });
  
  // ==================== Parsed Items State (Sales) ====================
  const [isParsedItemsOpen, setIsParsedItemsOpen] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [parsedFilename, setParsedFilename] = useState("");
  const [newImportSeries, setNewImportSeries] = useState("");
  const [salesProjectName, setSalesProjectName] = useState("");
  const [dimensionUnit, setDimensionUnit] = useState("m");
  const [importAllImages, setImportAllImages] = useState([]);
  
  // ==================== Manual Add Type ====================
  const [isManualAddOpen, setIsManualAddOpen] = useState(false);
  const [manualAddData, setManualAddData] = useState({
    name: "",
    material: "",
    series: "",
    color: "",
    glass_spec: "",
    extra_specs: "",
    width_m: "",
    height_m: "",
    cost: "",
    image_data: null
  });
  const [isManualAddSubmitting, setIsManualAddSubmitting] = useState(false);
  
  // ==================== Price Trend Modal ====================
  const [trendModalType, setTrendModalType] = useState(null);
  
  // ==================== Duplicate Type Modal ====================
  const [duplicateModalType, setDuplicateModalType] = useState(null);
  const [duplicateNewName, setDuplicateNewName] = useState("");
  const [isDuplicating, setIsDuplicating] = useState(false);
  
  // ==================== Price Adjustment ====================
  const [isPriceAdjustOpen, setIsPriceAdjustOpen] = useState(false);
  const [priceAdjustData, setPriceAdjustData] = useState({
    percentage: "",
    applyTo: "all",
    filterMaterial: "",
    filterColor: "",
    filterName: ""
  });
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [selectedTypeIds, setSelectedTypeIds] = useState([]);
  
  // ==================== Adjustment History ====================
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isUndoing, setIsUndoing] = useState(null);
  
  // ==================== WindoorCraft States ====================
  const [windoorCraftLoading, setWindoorCraftLoading] = useState(false);
  const [windoorCraftParsedData, setWindoorCraftParsedData] = useState(null);
  const [isWindoorCraftModalOpen, setIsWindoorCraftModalOpen] = useState(false);
  const [isWindoorCraftSaving, setIsWindoorCraftSaving] = useState(false);
  
  // ==================== Installation & PDR ====================
  const [installationData, setInstallationData] = useState({ 
    cosM2i: '123', 
    emoXM2: '0.29', 
    selloXM2: '2.62', 
    destaXM2: '1.99', 
    eppXM2: '0.57', 
    utiPercent: '11',
    adicional: '0'
  });
  const [pdrData, setPdrData] = useState({ quantity: '', unitValue: '' });
  const [totalSupplyValue, setTotalSupplyValue] = useState(0);
  
  // ==================== PDF/History Modals ====================
  const [isPDFExportModalOpen, setIsPDFExportModalOpen] = useState(false);
  const [isQuoteHistoryOpen, setIsQuoteHistoryOpen] = useState(false);

  // ==================== Data Fetching Functions ====================
  
  const fetchMaterials = useCallback(async () => {
    try {
      const response = await api.get(`${API}/materials`);
      setMaterials(response.data.materials);
      setAluminumSeries(response.data.aluminum_series);
      setAvailableColors(response.data.colors || []);
    } catch (error) {
      console.error("Error fetching materials:", error?.message || error);
    }
  }, []);

  const fetchQuoteTypes = useCallback(async () => {
    try {
      const response = await api.get(`${API}/quote-types`, { withCredentials: true });
      setQuoteTypes(response.data);
    } catch (error) {
      console.error("Error fetching quote types:", error?.message || error);
    }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await api.get(`${API}/dashboard-stats`, { withCredentials: true });
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error?.message || error);
    }
  }, []);

  const fetchSalesFactories = useCallback(async () => {
    try {
      const response = await api.get(`${API}/quote-types/factories/list`, { withCredentials: true });
      setSalesFactories(response.data.factories || []);
    } catch (error) {
      console.error("Error fetching sales factories:", error?.message || error);
    }
  }, []);

  const fetchSalesFilterOptions = useCallback(async () => {
    try {
      const response = await api.get(`${API}/quote-types/filters/options`, { withCredentials: true });
      setAvailableSalesGlassSpecs(response.data.glass_specs || []);
      setAvailableSalesExtrasSpecs(response.data.extra_specs || []);
    } catch (error) {
      console.error("Error fetching filter options:", error?.message || error);
    }
  }, []);

  const fetchAllSalesData = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      fetchMaterials(), 
      fetchQuoteTypes(), 
      fetchDashboardStats(), 
      fetchSalesFactories(), 
      fetchSalesFilterOptions()
    ]);
    setIsLoading(false);
  }, [fetchMaterials, fetchQuoteTypes, fetchDashboardStats, fetchSalesFactories, fetchSalesFilterOptions]);

  // ==================== Helper Functions ====================
  
  const resetNewQuote = useCallback(() => {
    setNewQuote({ width: "", height: "", cost: "", notes: "" });
  }, []);

  const openTypeDetail = useCallback((type) => {
    setDetailType(type);
    setIsDetailOpen(true);
  }, []);

  const closeTypeDetail = useCallback(() => {
    setDetailType(null);
    setIsDetailOpen(false);
  }, []);

  const resetManualAddData = useCallback(() => {
    setManualAddData({
      name: "",
      material: "",
      series: "",
      color: "",
      glass_spec: "",
      extra_specs: "",
      width_m: "",
      height_m: "",
      cost: "",
      image_data: null
    });
  }, []);

  const clearSalesFilters = useCallback(() => {
    setSalesProjectFilter("all");
    setSalesGlassFilter("all");
    setSalesExtrasFilter("all");
  }, []);

  // ==================== Return All State and Functions ====================
  return {
    // Core data
    quoteTypes, setQuoteTypes,
    dashboardStats, setDashboardStats,
    materials, setMaterials,
    aluminumSeries, setAluminumSeries,
    availableColors,
    salesFactories, setSalesFactories,
    isLoading, setIsLoading,
    
    // Filters
    salesProjectFilter, setSalesProjectFilter,
    salesGlassFilter, setSalesGlassFilter,
    salesExtrasFilter, setSalesExtrasFilter,
    availableSalesGlassSpecs, setAvailableSalesGlassSpecs,
    availableSalesExtrasSpecs, setAvailableSalesExtrasSpecs,
    clearSalesFilters,
    
    // Type detail
    selectedType, setSelectedType,
    detailType, setDetailType,
    isDetailOpen, setIsDetailOpen,
    openTypeDetail, closeTypeDetail,
    
    // Add series/quote modals
    isAddSeriesOpen, setIsAddSeriesOpen,
    isAddQuoteOpen, setIsAddQuoteOpen,
    newSeriesName, setNewSeriesName,
    addQuoteType, setAddQuoteType,
    newQuote, setNewQuote,
    resetNewQuote,
    
    // Parsed items
    isParsedItemsOpen, setIsParsedItemsOpen,
    parsedItems, setParsedItems,
    parsedFilename, setParsedFilename,
    newImportSeries, setNewImportSeries,
    salesProjectName, setSalesProjectName,
    dimensionUnit, setDimensionUnit,
    importAllImages, setImportAllImages,
    
    // Manual add
    isManualAddOpen, setIsManualAddOpen,
    manualAddData, setManualAddData,
    isManualAddSubmitting, setIsManualAddSubmitting,
    resetManualAddData,
    
    // Price trend
    trendModalType, setTrendModalType,
    
    // Duplicate
    duplicateModalType, setDuplicateModalType,
    duplicateNewName, setDuplicateNewName,
    isDuplicating, setIsDuplicating,
    
    // Price adjustment
    isPriceAdjustOpen, setIsPriceAdjustOpen,
    priceAdjustData, setPriceAdjustData,
    isAdjusting, setIsAdjusting,
    selectedTypeIds, setSelectedTypeIds,
    
    // Adjustment history
    isHistoryOpen, setIsHistoryOpen,
    adjustmentHistory, setAdjustmentHistory,
    isLoadingHistory, setIsLoadingHistory,
    isUndoing, setIsUndoing,
    
    // WindoorCraft
    windoorCraftLoading, setWindoorCraftLoading,
    windoorCraftParsedData, setWindoorCraftParsedData,
    isWindoorCraftModalOpen, setIsWindoorCraftModalOpen,
    isWindoorCraftSaving, setIsWindoorCraftSaving,
    
    // Installation & PDR
    installationData, setInstallationData,
    pdrData, setPdrData,
    totalSupplyValue, setTotalSupplyValue,
    
    // PDF/History modals
    isPDFExportModalOpen, setIsPDFExportModalOpen,
    isQuoteHistoryOpen, setIsQuoteHistoryOpen,
    
    // Data fetching
    fetchMaterials,
    fetchQuoteTypes,
    fetchDashboardStats,
    fetchSalesFactories,
    fetchSalesFilterOptions,
    fetchAllSalesData
  };
}

export default useSalesState;
